import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const SERVICE_IMAGES = [
  "https://readdy.ai/api/search-image?query=crowdfunding%20campaign%20launch%20event%20with%20excited%20backers%20and%20product%20display%2C%20modern%20startup%20office%20with%20large%20screens%20showing%20funding%20progress%20bar%20reaching%20goal%2C%20enthusiastic%20team%20celebrating%20milestone%2C%20clean%20bright%20workspace%20with%20white%20and%20navy%20blue%20tones%2C%20high%20resolution%20corporate%20photography&width=600&height=400&seq=svc011&orientation=landscape",
  "https://readdy.ai/api/search-image?query=e-commerce%20online%20shopping%20platform%20on%20laptop%20and%20smartphone%20screens%2C%20product%20listings%20with%20clean%20minimal%20design%2C%20digital%20marketplace%20interface%2C%20modern%20home%20office%20desk%20setup%20with%20packages%20ready%20for%20shipping%2C%20bright%20natural%20lighting%2C%20high%20resolution%20photography&width=600&height=400&seq=svc012&orientation=landscape",
  "https://readdy.ai/api/search-image?query=social%20media%20marketing%20team%20analyzing%20campaign%20performance%20on%20multiple%20screens%2C%20influencer%20collaboration%20content%20creation%20studio%2C%20digital%20advertising%20dashboard%20with%20engagement%20metrics%2C%20modern%20creative%20agency%20office%20with%20bright%20lighting%20and%20white%20walls%2C%20high%20resolution%20photography&width=600&height=400&seq=svc013&orientation=landscape",
  "https://readdy.ai/api/search-image?query=legal%20compliance%20documents%20and%20certification%20papers%20on%20desk%2C%20professional%20business%20setting%2C%20regulatory%20review%20meeting%2C%20official%20stamps%20and%20seals%2C%20clean%20organized%20office%2C%20high%20resolution%20corporate%20photography&width=600&height=400&seq=svc014&orientation=landscape",
  "https://readdy.ai/api/search-image?query=customer%20support%20team%20in%20modern%20call%20center%2C%20friendly%20professionals%20with%20headsets%2C%20multiple%20screens%20showing%20customer%20data%2C%20bright%20welcoming%20office%20environment%2C%20team%20collaboration%2C%20high%20resolution%20corporate%20photography&width=600&height=400&seq=svc015&orientation=landscape",
];

export default function ServicesSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    children?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      image: SERVICE_IMAGES[0],
      title: t("services.s1Title"),
      titleEn: t("services.s1TitleEn"),
    },
    {
      image: SERVICE_IMAGES[1],
      title: t("services.s2Title"),
      titleEn: t("services.s2TitleEn"),
    },
    {
      image: SERVICE_IMAGES[2],
      title: t("services.s3Title"),
      titleEn: t("services.s3TitleEn"),
    },
    {
      image: SERVICE_IMAGES[3],
      title: t("services.s4Title"),
      titleEn: t("services.s4TitleEn"),
    },
    {
      image: SERVICE_IMAGES[4],
      title: t("services.s5Title"),
      titleEn: t("services.s5TitleEn"),
    },
  ];

  return (
    <section id="services" className="py-24 md:py-36 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 text-center mb-16 md:mb-20">
          <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-4">
            {t("services.label")} / {t("services.labelJa")}
          </p>
          <h2 className="text-3xl md:text-5xl font-black text-[#1a2332] leading-tight mb-2">
            {t("services.title")}
          </h2>
          <h2 className="text-3xl md:text-5xl font-black text-[#1a2332] leading-tight mb-4">
            {t("services.title2")}
          </h2>
          <p className="text-sm text-gray-400 tracking-widest">{t("services.titleEn")}</p>
        </div>

        {/* Photo Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((service, i) => (
            <div
              key={i}
              className={`animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 ${i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="group rounded-2xl overflow-hidden bg-[#fafafa] hover:bg-white transition-all duration-300 h-full flex flex-col">
                {/* Photo */}
                <div className="w-full h-52 md:h-60 overflow-hidden flex-shrink-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Text */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-base md:text-lg font-bold text-[#1a2332] mb-1">{service.title}</h3>
                  <p className="text-xs text-gray-400 tracking-wider">{service.titleEn}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 flex flex-col sm:flex-row items-center justify-center gap-4 mt-12 md:mt-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-3 bg-[#1a2332] text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-[#2a3342] transition-colors duration-300 whitespace-nowrap"
          >
            <span>{t("services.viewAll")}</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to="/process"
            className="inline-flex items-center gap-3 border border-[#1a2332] text-[#1a2332] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#1a2332] hover:text-white transition-colors duration-300 whitespace-nowrap"
          >
            <i className="ri-route-line"></i>
            <span>{t("process.navLink")}</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
